import type { JsonObject } from "@inf-monkeys-tech/monkeys";
import {
  Braces,
  Check,
  Copy,
  Hash,
  Link2,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Settings2,
  Sparkles,
  Trash2,
  Type,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "../primitives";
import type { DesignCapabilityRenderer } from "./contracts";
import { isJsonObject } from "./contracts";

export const FEATURE_COLUMN_MANAGER_SCHEMA =
  "monkeys-design://render-model/feature-column-manager/v2";

const JSON_SCHEMA_DIALECT = "https://json-schema.org/draft/2020-12/schema";
const labelProperties = {
  label: { type: "string" },
  labelKey: { type: "string", minLength: 1 },
};
const editableFeatureType = {
  enum: ["text", "img", "number", "relation", "json"],
};
const featureColumnType = { type: "string", minLength: 1, maxLength: 128 };
const columnConfig = { type: "object", additionalProperties: true };

export const FEATURE_COLUMN_MANAGER_RENDER_MODEL_SCHEMA: JsonObject = {
  $schema: JSON_SCHEMA_DIALECT,
  $id: FEATURE_COLUMN_MANAGER_SCHEMA,
  type: "object",
  additionalProperties: false,
  required: ["columns", "pagination", "disabled"],
  properties: {
    disabled: { type: "boolean" },
    canCreate: { type: "boolean" },
    canUpdate: { type: "boolean" },
    canDelete: { type: "boolean" },
    canAttach: { type: "boolean" },
    triggerLabelKey: { type: "string", minLength: 1 },
    titleKey: { type: "string", minLength: 1 },
    descriptionKey: { type: "string", minLength: 1 },
    createLabelKey: { type: "string", minLength: 1 },
    namePlaceholderKey: { type: "string", minLength: 1 },
    selectedCountKey: { type: "string", minLength: 1 },
    deleteLabelKey: { type: "string", minLength: 1 },
    duplicateLabelKey: { type: "string", minLength: 1 },
    editLabelKey: { type: "string", minLength: 1 },
    saveLabelKey: { type: "string", minLength: 1 },
    cancelLabelKey: { type: "string", minLength: 1 },
    emptyLabelKey: { type: "string", minLength: 1 },
    autoAttachLabelKey: { type: "string", minLength: 1 },
    expectedOntologyDefinitionVersion: { type: "integer", minimum: 1 },
    expectedViewDefinitionVersion: { type: "integer", minimum: 1 },
    scopeContainerViewId: { type: "string", minLength: 1 },
    canAttachToView: { type: "boolean" },
    types: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id"],
        properties: { id: editableFeatureType, ...labelProperties },
      },
    },
    ontologyOptions: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "label"],
        properties: {
          id: { type: "string", minLength: 1 },
          label: { type: "string", minLength: 1 },
        },
      },
    },
    columns: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: [
          "id",
          "name",
          "type",
          "definitionVersion",
          "sort",
          "attached",
        ],
        properties: {
          id: { type: "string", minLength: 1 },
          name: { type: "string", minLength: 1 },
          type: featureColumnType,
          config: columnConfig,
          definitionVersion: { type: "integer", minimum: 1 },
          sort: { type: "integer", minimum: 0 },
          attached: { type: "boolean" },
          readOnly: { type: "boolean" },
          description: { type: "string" },
        },
      },
    },
    pagination: {
      type: "object",
      additionalProperties: false,
      required: ["hasMore", "nextCursor", "loadingMore"],
      properties: {
        hasMore: { type: "boolean" },
        nextCursor: {
          oneOf: [{ type: "string", minLength: 1 }, { type: "null" }],
        },
        loadingMore: { type: "boolean" },
        errorCode: { type: "string", minLength: 1 },
      },
    },
  },
};

const strictIntent = (
  id: string,
  properties: JsonObject,
  required: string[],
): JsonObject => ({
  $schema: JSON_SCHEMA_DIALECT,
  $id: id,
  type: "object",
  additionalProperties: false,
  required,
  properties,
});

export const FEATURE_COLUMN_MANAGER_CREATE_INTENT_SCHEMA = strictIntent(
  "monkeys-design://intent/feature-column-manager-create/v1",
  {
    name: { type: "string", minLength: 1, maxLength: 512 },
    type: editableFeatureType,
    config: columnConfig,
    sort: { type: "integer", minimum: 0 },
    scopeContainerViewId: { type: "string", minLength: 1, maxLength: 256 },
    expectedOntologyDefinitionVersion: { type: "integer", minimum: 1 },
  },
  ["name", "type", "expectedOntologyDefinitionVersion"],
);

export const FEATURE_COLUMN_MANAGER_UPDATE_INTENT_SCHEMA = strictIntent(
  "monkeys-design://intent/feature-column-manager-update/v1",
  {
    columnId: { type: "string", minLength: 1, maxLength: 256 },
    expectedDefinitionVersion: { type: "integer", minimum: 1 },
    changes: {
      type: "object",
      additionalProperties: false,
      required: ["name", "type"],
      properties: {
        name: { type: "string", minLength: 1, maxLength: 512 },
        type: editableFeatureType,
        config: { oneOf: [columnConfig, { type: "null" }] },
      },
    },
  },
  ["columnId", "expectedDefinitionVersion", "changes"],
);

export const FEATURE_COLUMN_MANAGER_DELETE_INTENT_SCHEMA = strictIntent(
  "monkeys-design://intent/feature-column-manager-delete/v1",
  {
    columns: {
      type: "array",
      minItems: 1,
      maxItems: 500,
      uniqueItems: true,
      items: {
        type: "object",
        additionalProperties: false,
        required: ["columnId", "expectedDefinitionVersion"],
        properties: {
          columnId: { type: "string", minLength: 1, maxLength: 256 },
          expectedDefinitionVersion: { type: "integer", minimum: 1 },
        },
      },
    },
  },
  ["columns"],
);

export const FEATURE_COLUMN_MANAGER_ATTACHMENT_INTENT_SCHEMA = strictIntent(
  "monkeys-design://intent/feature-column-manager-attachment/v1",
  {
    columnId: { type: "string", minLength: 1, maxLength: 256 },
    attached: { type: "boolean" },
    expectedViewDefinitionVersion: { type: "integer", minimum: 1 },
  },
  ["columnId", "attached", "expectedViewDefinitionVersion"],
);

export const FEATURE_COLUMN_MANAGER_LOAD_MORE_INTENT_SCHEMA = strictIntent(
  "monkeys-design://intent/feature-column-manager-load-more/v1",
  { cursor: { type: "string", minLength: 1 } },
  ["cursor"],
);

export const FEATURE_COLUMN_MANAGER_OUTPUT_PORTS = [
  {
    name: "capability.feature-column-manager.create",
    schemaRef: "monkeys-design://intent/feature-column-manager-create/v1",
    schemaDocument: FEATURE_COLUMN_MANAGER_CREATE_INTENT_SCHEMA,
    allowedCommandKinds: ["domain-command"],
  },
  {
    name: "capability.feature-column-manager.update",
    schemaRef: "monkeys-design://intent/feature-column-manager-update/v1",
    schemaDocument: FEATURE_COLUMN_MANAGER_UPDATE_INTENT_SCHEMA,
    allowedCommandKinds: ["domain-command"],
  },
  {
    name: "capability.feature-column-manager.delete",
    schemaRef: "monkeys-design://intent/feature-column-manager-delete/v1",
    schemaDocument: FEATURE_COLUMN_MANAGER_DELETE_INTENT_SCHEMA,
    allowedCommandKinds: ["domain-command"],
  },
  {
    name: "capability.feature-column-manager.attachment-change",
    schemaRef: "monkeys-design://intent/feature-column-manager-attachment/v1",
    schemaDocument: FEATURE_COLUMN_MANAGER_ATTACHMENT_INTENT_SCHEMA,
    allowedCommandKinds: ["domain-command"],
  },
  {
    name: "capability.feature-column-manager.load-more",
    schemaRef: "monkeys-design://intent/feature-column-manager-load-more/v1",
    schemaDocument: FEATURE_COLUMN_MANAGER_LOAD_MORE_INTENT_SCHEMA,
    allowedCommandKinds: ["query-intent"],
  },
] as const;

type ColumnModel = {
  id: string;
  name: string;
  type: string;
  config?: JsonObject;
  definitionVersion: number;
  sort: number;
  attached: boolean;
  readOnly?: boolean;
  description?: string;
};

type FeatureColumnType = "text" | "img" | "number" | "relation" | "json";
type NumberFormatMode = "integer" | "decimal";
type RelationConfig = {
  allowCrossOntology: boolean;
  singleSelect: boolean;
  allowedOntologyIds: string[];
};

const normalizeFeatureColumnType = (value: string): FeatureColumnType =>
  value === "img" ||
  value === "number" ||
  value === "relation" ||
  value === "json"
    ? value
    : "text";

const readNumberConfig = (
  config?: JsonObject,
): { mode: NumberFormatMode; scale: number } => {
  const source = isJsonObject(config?.numberFormat)
    ? config.numberFormat
    : config;
  const rawMode = readText(source?.mode).toLowerCase();
  const rawScale = Number(source?.scale);
  const scale = Number.isFinite(rawScale)
    ? Math.max(0, Math.min(9, Math.trunc(rawScale)))
    : 2;
  const mode: NumberFormatMode =
    rawMode === "fixed" ||
    rawMode === "decimal" ||
    rawMode === "thousand_decimal" ||
    scale > 0
      ? "decimal"
      : "integer";
  return { mode, scale: mode === "decimal" ? scale || 2 : 0 };
};

const readRelationConfig = (config?: JsonObject): RelationConfig => {
  const relation = isJsonObject(config?.relation) ? config.relation : {};
  return {
    allowCrossOntology: relation.allowCrossOntology !== false,
    singleSelect: relation.singleSelect === true,
    allowedOntologyIds: Array.isArray(relation.allowedOntologyIds)
      ? relation.allowedOntologyIds
          .filter(
            (value): value is string =>
              typeof value === "string" && Boolean(value.trim()),
          )
          .map((value) => value.trim())
      : [],
  };
};

const buildColumnConfig = (input: {
  type: FeatureColumnType;
  numberMode: NumberFormatMode;
  numberScale: string;
  relationAllowCrossOntology: boolean;
  relationSingleSelect: boolean;
  relationAllowedOntologyIds: readonly string[];
}): JsonObject | undefined => {
  if (input.type === "number") {
    const scale = Math.max(
      0,
      Math.min(9, Math.trunc(Number(input.numberScale) || 0)),
    );
    return {
      numberFormat: {
        mode: input.numberMode === "decimal" ? "fixed" : "integer",
        scale: input.numberMode === "decimal" ? scale || 2 : 0,
      },
    };
  }
  if (input.type === "relation") {
    return {
      relation: {
        allowCrossOntology: input.relationAllowCrossOntology,
        singleSelect: input.relationSingleSelect,
        allowedOntologyIds: [
          ...new Set(
            input.relationAllowedOntologyIds
              .map((id) => id.trim())
              .filter(Boolean),
          ),
        ],
      },
    };
  }
  return undefined;
};

const duplicateName = (
  sourceName: string,
  columns: readonly ColumnModel[],
  suffix: string,
): string => {
  const base =
    (sourceName.trim() || "Column") + " " + (suffix.trim() || "Copy");
  const names = new Set(
    columns.map((column) => column.name.trim().toLocaleLowerCase()),
  );
  if (!names.has(base.toLocaleLowerCase())) return base;
  let index = 2;
  while (names.has((base + " " + index).toLocaleLowerCase())) index += 1;
  return base + " " + index;
};

const readText = (value: unknown): string =>
  typeof value === "string" ? value : "";
const copy = (
  model: JsonObject,
  key: string,
  fallback: string,
  translate: (
    key: string,
    values?: Record<string, string | number | boolean>,
  ) => string,
) => {
  const semanticKey = readText(model[key]);
  return semanticKey ? translate(semanticKey) : fallback;
};

const columnsFromModel = (value: unknown): ColumnModel[] =>
  Array.isArray(value)
    ? value.flatMap((candidate) => {
        if (!isJsonObject(candidate)) return [];
        const id = readText(candidate.id);
        const name = readText(candidate.name);
        const type = readText(candidate.type).trim();
        const definitionVersion = Number(candidate.definitionVersion);
        const sort = Number(candidate.sort);
        if (
          !id ||
          !name ||
          !type ||
          type.length > 128 ||
          !Number.isSafeInteger(definitionVersion) ||
          definitionVersion < 1
        )
          return [];
        return [
          {
            id,
            name,
            type,
            definitionVersion,
            sort: Number.isSafeInteger(sort) && sort >= 0 ? sort : 0,
            attached: candidate.attached === true,
            ...(isJsonObject(candidate.config)
              ? { config: candidate.config }
              : {}),
            ...(candidate.readOnly === true ? { readOnly: true } : {}),
            ...(readText(candidate.description)
              ? { description: readText(candidate.description) }
              : {}),
          },
        ];
      })
    : [];

type OntologyOption = { id: string; label: string };

const ontologyOptionsFromModel = (value: unknown): OntologyOption[] =>
  Array.isArray(value)
    ? value.flatMap((candidate) => {
        if (!isJsonObject(candidate)) return [];
        const id = readText(candidate.id).trim();
        const label = readText(candidate.label).trim();
        return id && label ? [{ id, label }] : [];
      })
    : [];

const FeatureColumnToggleRow = ({
  checked,
  disabled,
  icon: Icon,
  title,
  description,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  icon: typeof Link2;
  title: string;
  description?: string;
  onCheckedChange: (checked: boolean) => void;
}) => (
  <div
    className={
      "flex items-center gap-2.5 rounded-lg border px-2.5 py-2.5 transition-colors " +
      (checked
        ? "border-primary/20 bg-primary/[0.045] "
        : "border-border/60 bg-background/90 ") +
      (disabled
        ? "cursor-not-allowed opacity-55"
        : "cursor-pointer hover:border-border hover:bg-accent/25")
    }
    onClick={() => {
      if (!disabled) onCheckedChange(!checked);
    }}
  >
    <div
      className={
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/35 text-muted-foreground " +
        (checked ? "bg-background text-foreground" : "")
      }
    >
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1 pr-1">
      <div className="whitespace-nowrap text-[13px] font-medium leading-5 text-foreground">
        {title}
      </div>
      {description ? (
        <div className="mt-0.5 truncate text-[11px] leading-4 text-muted-foreground">
          {description}
        </div>
      ) : null}
    </div>
    <Switch
      checked={checked}
      disabled={disabled}
      className="h-5 w-9 shrink-0 data-[state=checked]:bg-foreground data-[state=unchecked]:bg-muted"
      onClick={(event) => event.stopPropagation()}
      onCheckedChange={onCheckedChange}
    />
  </div>
);

const RelationConfigFields = ({
  allowCrossOntology,
  singleSelect,
  allowedOntologyIds,
  ontologyOptions,
  disabled,
  translate,
  onAllowCrossOntologyChange,
  onSingleSelectChange,
  onAllowedOntologyIdsChange,
}: {
  allowCrossOntology: boolean;
  singleSelect: boolean;
  allowedOntologyIds: readonly string[];
  ontologyOptions: readonly OntologyOption[];
  disabled?: boolean;
  translate: (
    key: string,
    values?: Record<string, string | number | boolean>,
  ) => string;
  onAllowCrossOntologyChange: (checked: boolean) => void;
  onSingleSelectChange: (checked: boolean) => void;
  onAllowedOntologyIdsChange: (ids: string[]) => void;
}) => {
  const [query, setQuery] = useState("");
  const selected = new Set(allowedOntologyIds);
  const filtered = ontologyOptions.filter((option) => {
    const keyword = query.trim().toLocaleLowerCase();
    return (
      !keyword ||
      option.id.toLocaleLowerCase().includes(keyword) ||
      option.label.toLocaleLowerCase().includes(keyword)
    );
  });
  const toggle = (id: string, checked: boolean) =>
    onAllowedOntologyIdsChange(
      checked
        ? [...new Set([...allowedOntologyIds, id])]
        : allowedOntologyIds.filter((candidate) => candidate !== id),
    );

  return (
    <div className="grid gap-2" onClick={(event) => event.stopPropagation()}>
      <div className="grid gap-2 sm:grid-cols-2">
        <FeatureColumnToggleRow
          checked={allowCrossOntology}
          disabled={disabled}
          icon={Link2}
          title={translate(
            "components.data-v2.feature-column.relation-cross-ontology",
          )}
          onCheckedChange={onAllowCrossOntologyChange}
        />
        <FeatureColumnToggleRow
          checked={singleSelect}
          disabled={disabled}
          icon={Type}
          title={translate("components.data-v2.feature-column.relation-single")}
          onCheckedChange={onSingleSelectChange}
        />
      </div>
      <div
        className={
          "overflow-hidden rounded-xl border border-border/60 bg-muted/[0.16] " +
          (disabled ? "opacity-60" : "")
        }
      >
        <div className="flex items-center justify-between gap-2 border-b border-border/50 bg-background/70 px-3 py-2">
          <div className="text-xs font-medium text-muted-foreground">
            {translate(
              "components.data-v2.feature-column.relation-allowed-ontologies-select-title",
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 rounded-md px-2 text-xs"
            disabled={disabled || allowedOntologyIds.length === 0}
            onClick={() => onAllowedOntologyIdsChange([])}
          >
            {translate("common.actions.clear")}
          </Button>
        </div>
        <div className="space-y-2.5 p-3">
          <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-background px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input
              value={query}
              disabled={disabled}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={translate(
                "components.data-v2.feature-column.relation-allowed-ontologies-search",
              )}
              className="h-6 border-0 bg-transparent px-0 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          {allowedOntologyIds.length ? (
            <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto pr-1">
              {allowedOntologyIds.map((id) => {
                const option = ontologyOptions.find(
                  (candidate) => candidate.id === id,
                );
                return (
                  <Badge
                    key={id}
                    variant="secondary"
                    className="flex items-center gap-1 rounded-full bg-background px-2.5 py-1 text-[11px] font-medium text-foreground"
                  >
                    <span className="max-w-[10rem] truncate">
                      {option?.label || id}
                    </span>
                    <button
                      type="button"
                      disabled={disabled}
                      className="rounded-full p-0.5 hover:bg-black/5 dark:hover:bg-white/10"
                      onClick={() => toggle(id, false)}
                      aria-label={translate("common.actions.clear")}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          ) : null}
          <div className="max-h-52 space-y-1 overflow-y-auto pr-1">
            {filtered.length ? (
              filtered.map((option) => {
                const checked = selected.has(option.id);
                return (
                  <label
                    key={option.id}
                    className={
                      "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors " +
                      (checked
                        ? "border-primary/20 bg-primary/[0.045]"
                        : "border-border/60 bg-background hover:border-border hover:bg-accent/25")
                    }
                  >
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onCheckedChange={(next) =>
                        toggle(option.id, next === true)
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {option.label}
                      </div>
                      <div className="truncate text-[11px] text-muted-foreground">
                        {option.id}
                      </div>
                    </div>
                  </label>
                );
              })
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 bg-background px-3 py-3 text-sm text-muted-foreground">
                {translate(
                  "components.data-v2.feature-column.relation-allowed-ontologies-select-empty",
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FeatureColumnManagerCapabilityRenderer: DesignCapabilityRenderer =
  ({ model, translate, emitIntent }) => {
    const columns = useMemo(
      () => columnsFromModel(model.columns),
      [model.columns],
    );
    const ontologyOptions = useMemo(
      () => ontologyOptionsFromModel(model.ontologyOptions),
      [model.ontologyOptions],
    );
    const typeOptions = useMemo(
      () =>
        (Array.isArray(model.types) ? model.types : []).flatMap((candidate) => {
          if (!isJsonObject(candidate)) return [];
          const id = readText(candidate.id);
          if (!id) return [];
          const labelKey = readText(candidate.labelKey);
          return [
            {
              id,
              label: labelKey
                ? translate(labelKey)
                : readText(candidate.label) || id,
            },
          ];
        }),
      [model.types, translate],
    );
    const [open, setOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newType, setNewType] = useState<FeatureColumnType>("text");
    const [newNumberMode, setNewNumberMode] =
      useState<NumberFormatMode>("integer");
    const [newNumberScale, setNewNumberScale] = useState("2");
    const [newRelationAllowCrossOntology, setNewRelationAllowCrossOntology] =
      useState(true);
    const [newRelationSingleSelect, setNewRelationSingleSelect] =
      useState(false);
    const [newRelationAllowedOntologyIds, setNewRelationAllowedOntologyIds] =
      useState<string[]>([]);
    const [autoAttach, setAutoAttach] = useState(true);
    const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
      new Set(),
    );
    const [lastSelectedId, setLastSelectedId] = useState("");
    const [editingId, setEditingId] = useState("");
    const [editingName, setEditingName] = useState("");
    const [editingType, setEditingType] = useState<FeatureColumnType>("text");
    const [editingNumberMode, setEditingNumberMode] =
      useState<NumberFormatMode>("integer");
    const [editingNumberScale, setEditingNumberScale] = useState("2");
    const [
      editingRelationAllowCrossOntology,
      setEditingRelationAllowCrossOntology,
    ] = useState(true);
    const [editingRelationSingleSelect, setEditingRelationSingleSelect] =
      useState(false);
    const [
      editingRelationAllowedOntologyIds,
      setEditingRelationAllowedOntologyIds,
    ] = useState<string[]>([]);
    const [deleteTargets, setDeleteTargets] = useState<ColumnModel[]>([]);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
      if (!open) {
        setNewName("");
        setNewType("text");
        setNewNumberMode("integer");
        setNewNumberScale("2");
        setNewRelationAllowCrossOntology(true);
        setNewRelationSingleSelect(false);
        setNewRelationAllowedOntologyIds([]);
        setAutoAttach(true);
        setSelectedIds(new Set());
        setLastSelectedId("");
        setEditingId("");
        setDeleteTargets([]);
        setConfirmDeleteOpen(false);
      }
    }, [open]);

    const expectedOntologyDefinitionVersion = Number(
      model.expectedOntologyDefinitionVersion,
    );
    const canCreate = model.disabled !== true && model.canCreate !== false;
    const canUpdate = model.disabled !== true && model.canUpdate !== false;
    const canDelete = model.disabled !== true && model.canDelete !== false;
    const canAttach = model.disabled !== true && model.canAttach !== false;
    const triggerLabel = copy(
      model,
      "triggerLabelKey",
      "Feature columns",
      translate,
    );
    const selectColumn = (
      column: ColumnModel,
      event: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
    ) => {
      if (column.readOnly) return;
      if (event.shiftKey && lastSelectedId) {
        const from = columns.findIndex(
          (candidate) => candidate.id === lastSelectedId,
        );
        const to = columns.findIndex((candidate) => candidate.id === column.id);
        if (from >= 0 && to >= 0) {
          const next = new Set(selectedIds);
          columns
            .slice(Math.min(from, to), Math.max(from, to) + 1)
            .filter((candidate) => !candidate.readOnly)
            .forEach((candidate) => next.add(candidate.id));
          setSelectedIds(next);
          return;
        }
      }
      if (event.metaKey || event.ctrlKey) {
        const next = new Set(selectedIds);
        if (next.has(column.id)) next.delete(column.id);
        else next.add(column.id);
        setSelectedIds(next);
        setLastSelectedId(column.id);
        return;
      }
      setSelectedIds(new Set([column.id]));
      setLastSelectedId(column.id);
    };
    const createColumn = (source?: ColumnModel) => {
      const type = source ? normalizeFeatureColumnType(source.type) : newType;
      const fallbackTypeLabel =
        typeOptions.find(({ id }) => id === type)?.label || type;
      const name = source
        ? duplicateName(
            source.name,
            columns,
            copy(model, "duplicateLabelKey", "Copy", translate),
          )
        : (newName.trim() || fallbackTypeLabel + (columns.length + 1)).trim();
      if (
        !name ||
        !Number.isSafeInteger(expectedOntologyDefinitionVersion) ||
        expectedOntologyDefinitionVersion < 1
      )
        return;
      const config =
        source?.config ??
        buildColumnConfig({
          type,
          numberMode: newNumberMode,
          numberScale: newNumberScale,
          relationAllowCrossOntology: newRelationAllowCrossOntology,
          relationSingleSelect: newRelationSingleSelect,
          relationAllowedOntologyIds: newRelationAllowedOntologyIds,
        });
      emitIntent("capability.feature-column-manager.create", {
        name,
        type,
        expectedOntologyDefinitionVersion,
        ...(config ? { config } : {}),
        ...(source ? { sort: source.sort + 1 } : {}),
        ...(autoAttach && readText(model.scopeContainerViewId)
          ? { scopeContainerViewId: readText(model.scopeContainerViewId) }
          : {}),
      });
      if (!source) setNewName("");
    };

    const deleteColumns = (targets: ColumnModel[]) => {
      if (!canDelete) return;
      const deletable = targets.filter((column) => !column.readOnly);
      if (!deletable.length) return;
      emitIntent("capability.feature-column-manager.delete", {
        columns: deletable.map((column) => ({
          columnId: column.id,
          expectedDefinitionVersion: column.definitionVersion,
        })),
      });
      setSelectedIds(new Set());
      setLastSelectedId("");
      setDeleteTargets([]);
      setConfirmDeleteOpen(false);
    };

    const openDeleteConfirm = (targets: ColumnModel[]) => {
      const deletable = targets.filter((column) => !column.readOnly);
      if (!canDelete || !deletable.length) return;
      setDeleteTargets(deletable);
      setConfirmDeleteOpen(true);
    };

    const startEdit = (column: ColumnModel) => {
      if (!canUpdate || column.readOnly) return;
      const number = readNumberConfig(column.config);
      const relation = readRelationConfig(column.config);
      setEditingId(column.id);
      setEditingName(column.name);
      setEditingType(normalizeFeatureColumnType(column.type));
      setEditingNumberMode(number.mode);
      setEditingNumberScale(String(number.scale));
      setEditingRelationAllowCrossOntology(relation.allowCrossOntology);
      setEditingRelationSingleSelect(relation.singleSelect);
      setEditingRelationAllowedOntologyIds(relation.allowedOntologyIds);
    };

    const commitEdit = (column: ColumnModel) => {
      const name = editingName.trim();
      if (!name) return;
      const config = buildColumnConfig({
        type: editingType,
        numberMode: editingNumberMode,
        numberScale: editingNumberScale,
        relationAllowCrossOntology: editingRelationAllowCrossOntology,
        relationSingleSelect: editingRelationSingleSelect,
        relationAllowedOntologyIds: editingRelationAllowedOntologyIds,
      });
      emitIntent("capability.feature-column-manager.update", {
        columnId: column.id,
        expectedDefinitionVersion: column.definitionVersion,
        changes: { name, type: editingType, config: config ?? null },
      });
      setEditingId("");
    };

    const pagination = isJsonObject(model.pagination) ? model.pagination : {};
    return (
      <>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 shrink-0 gap-2 px-2 text-foreground 2xl:px-3"
              aria-label={triggerLabel}
              title={triggerLabel}
              disabled={model.disabled === true}
            >
              <Settings2 className="h-4 w-4" />
              <span className="sr-only 2xl:not-sr-only">{triggerLabel}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            side="bottom"
            sideOffset={8}
            collisionPadding={16}
            className="z-[80] flex max-h-[min(70vh,32rem)] w-[min(28rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-xl border border-border bg-background p-0 text-foreground shadow-[0_14px_32px_-20px_rgba(15,23,42,0.24)] outline-none"
          >
            <div className="shrink-0 border-b border-border/60 px-3.5 py-2.5">
              <div className="flex items-start gap-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/35 text-muted-foreground">
                  <Settings2 className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h2 className="truncate text-sm font-semibold text-foreground">
                      {copy(model, "titleKey", triggerLabel, translate)}
                    </h2>
                    {columns.length ? (
                      <Badge
                        variant="secondary"
                        className="rounded-full bg-muted/70 px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
                      >
                        {columns.length}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">
                    {copy(
                      model,
                      "descriptionKey",
                      "Create and manage feature columns.",
                      translate,
                    )}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 rounded-md text-muted-foreground"
                  onClick={() => setOpen(false)}
                  aria-label={copy(model, "cancelLabelKey", "Close", translate)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="shrink-0 space-y-1.5 px-2.5 pt-2">
              <div className="grid gap-2 rounded-xl border border-border/60 bg-muted/[0.18] p-2 sm:grid-cols-[minmax(0,1fr)_5.5rem_auto]">
                <Input
                  value={newName}
                  onChange={(event) => setNewName(event.currentTarget.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") createColumn();
                  }}
                  placeholder={copy(
                    model,
                    "namePlaceholderKey",
                    "Column name",
                    translate,
                  )}
                  className="h-8 rounded-lg border-border/70 bg-background shadow-none"
                />
                <Select
                  value={newType}
                  onValueChange={(value) =>
                    setNewType(normalizeFeatureColumnType(value))
                  }
                >
                  <SelectTrigger className="h-8 rounded-lg border-border/70 bg-background shadow-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {(typeOptions.length
                      ? typeOptions
                      : ["text", "img", "number", "relation", "json"].map(
                          (id) => ({ id, label: id }),
                        )
                    ).map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 rounded-lg px-3"
                  disabled={!canCreate}
                  onClick={() => createColumn()}
                >
                  <Plus className="h-4 w-4" />
                  {copy(model, "createLabelKey", "Create", translate)}
                </Button>
              </div>
              {newType === "number" ? (
                <div
                  className={
                    "grid gap-2 " +
                    (newNumberMode === "decimal"
                      ? "sm:grid-cols-[8rem_6rem]"
                      : "sm:grid-cols-[8rem]")
                  }
                >
                  <Select
                    value={newNumberMode}
                    onValueChange={(value) =>
                      setNewNumberMode(
                        value === "decimal" ? "decimal" : "integer",
                      )
                    }
                  >
                    <SelectTrigger className="h-8 rounded-lg border-border/70 bg-background shadow-none">
                      <SelectValue
                        placeholder={translate(
                          "components.data-v2.feature-column.number-format",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent className="z-[100]">
                      <SelectItem value="integer">
                        {translate(
                          "components.data-v2.feature-column.number-integer",
                        )}
                      </SelectItem>
                      <SelectItem value="decimal">
                        {translate(
                          "components.data-v2.feature-column.number-decimal",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {newNumberMode === "decimal" ? (
                    <Input
                      value={newNumberScale}
                      onChange={(event) =>
                        setNewNumberScale(
                          event.currentTarget.value
                            .replace(/[^\d]/g, "")
                            .slice(0, 1),
                        )
                      }
                      placeholder={translate(
                        "components.data-v2.feature-column.decimal-places",
                      )}
                      className="h-8 rounded-lg border-border/70 bg-background shadow-none"
                    />
                  ) : null}
                </div>
              ) : null}
              {newType === "relation" ? (
                <RelationConfigFields
                  allowCrossOntology={newRelationAllowCrossOntology}
                  singleSelect={newRelationSingleSelect}
                  allowedOntologyIds={newRelationAllowedOntologyIds}
                  ontologyOptions={ontologyOptions}
                  disabled={!canCreate}
                  translate={translate}
                  onAllowCrossOntologyChange={setNewRelationAllowCrossOntology}
                  onSingleSelectChange={setNewRelationSingleSelect}
                  onAllowedOntologyIdsChange={setNewRelationAllowedOntologyIds}
                />
              ) : null}
              {model.canAttachToView === true ? (
                <FeatureColumnToggleRow
                  checked={autoAttach}
                  disabled={!canAttach}
                  icon={Sparkles}
                  title={copy(
                    model,
                    "autoAttachLabelKey",
                    "Attach to current view",
                    translate,
                  )}
                  description={translate(
                    "components.data-v2.feature-column.auto-attach-desc",
                  )}
                  onCheckedChange={setAutoAttach}
                />
              ) : null}
              {selectedIds.size ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/15 bg-primary/[0.05] px-3 py-2">
                  <span className="text-xs font-medium">
                    {translate(
                      readText(model.selectedCountKey) ||
                        "common.actions.selected",
                      { count: selectedIds.size },
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="h-7 rounded-md px-2.5"
                      disabled={!canDelete}
                      onClick={() =>
                        openDeleteConfirm(
                          columns.filter((column) =>
                            selectedIds.has(column.id),
                          ),
                        )
                      }
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {translate(
                        "components.data-v2.feature-column.batch-delete",
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 rounded-md px-2.5"
                      onClick={() => {
                        setSelectedIds(new Set());
                        setLastSelectedId("");
                      }}
                    >
                      {translate("components.data-v2.feature-column.deselect")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2.5 pt-2">
              <div className="mb-1 flex items-center gap-1.5 px-0.5">
                <h3 className="truncate text-[13px] font-medium text-muted-foreground">
                  {triggerLabel}
                </h3>
                {columns.length ? (
                  <Badge
                    variant="secondary"
                    className="rounded-full bg-muted/70 px-1.5 py-0 text-[10px] font-medium text-muted-foreground"
                  >
                    {columns.length}
                  </Badge>
                ) : null}
              </div>
              {columns.length ? (
                <div className="space-y-1.5">
                  {columns.map((column) => {
                    const editing = editingId === column.id;
                    const selected = selectedIds.has(column.id);
                    const TypeIcon =
                      column.type === "img" ||
                      column.type === "image" ||
                      column.type === "images" ||
                      column.type === "media"
                        ? Paperclip
                        : column.type === "number"
                          ? Hash
                          : column.type === "relation"
                            ? Link2
                            : column.type === "json"
                              ? Braces
                              : Type;
                    return (
                      <div
                        key={column.id}
                        className={
                          "group flex items-center gap-2.5 rounded-lg border px-3 py-2.5 transition-colors " +
                          (selectedIds.has(column.id)
                            ? "border-primary/20 bg-primary/[0.045] "
                            : "border-border/60 bg-background ") +
                          (!editing && !column.readOnly
                            ? "cursor-pointer hover:border-border hover:bg-accent/25 "
                            : "") +
                          (editing ? "items-start" : "")
                        }
                        onClick={(event) => {
                          if (!editing) selectColumn(column, event);
                        }}
                      >
                        <div
                          className={
                            "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/35 text-muted-foreground " +
                            (selected ? "text-foreground" : "")
                          }
                        >
                          <TypeIcon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          {editing ? (
                            <div className="space-y-2.5">
                              <div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_7rem_auto]">
                                <Input
                                  value={editingName}
                                  onChange={(event) =>
                                    setEditingName(event.currentTarget.value)
                                  }
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter")
                                      commitEdit(column);
                                    if (event.key === "Escape")
                                      setEditingId("");
                                  }}
                                  onClick={(event) => event.stopPropagation()}
                                  autoFocus
                                  className="h-8 rounded-lg border-border/70 bg-background shadow-none"
                                />
                                <Select
                                  value={editingType}
                                  onValueChange={(value) =>
                                    setEditingType(
                                      normalizeFeatureColumnType(value),
                                    )
                                  }
                                >
                                  <SelectTrigger
                                    className="h-8 rounded-lg border-border/70 bg-background shadow-none"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="z-[100]">
                                    {(typeOptions.length
                                      ? typeOptions
                                      : [{ id: "text", label: "text" }]
                                    ).map((option) => (
                                      <SelectItem
                                        key={option.id}
                                        value={option.id}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div className="flex shrink-0 items-center justify-end gap-1.5">
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="h-8 rounded-md px-2.5"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      commitEdit(column);
                                    }}
                                    disabled={!editingName.trim()}
                                  >
                                    {copy(
                                      model,
                                      "saveLabelKey",
                                      "Save",
                                      translate,
                                    )}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-8 rounded-md px-2.5"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setEditingId("");
                                    }}
                                  >
                                    {copy(
                                      model,
                                      "cancelLabelKey",
                                      "Cancel",
                                      translate,
                                    )}
                                  </Button>
                                </div>
                              </div>
                              {editingType === "number" ? (
                                <div
                                  className={
                                    "grid gap-2 " +
                                    (editingNumberMode === "decimal"
                                      ? "sm:grid-cols-[8rem_6rem]"
                                      : "sm:grid-cols-[8rem]")
                                  }
                                >
                                  <Select
                                    value={editingNumberMode}
                                    onValueChange={(value) =>
                                      setEditingNumberMode(
                                        value === "decimal"
                                          ? "decimal"
                                          : "integer",
                                      )
                                    }
                                  >
                                    <SelectTrigger
                                      className="h-8 rounded-lg border-border/70 bg-background shadow-none"
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="z-[100]">
                                      <SelectItem value="integer">
                                        {translate(
                                          "components.data-v2.feature-column.number-integer",
                                        )}
                                      </SelectItem>
                                      <SelectItem value="decimal">
                                        {translate(
                                          "components.data-v2.feature-column.number-decimal",
                                        )}
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {editingNumberMode === "decimal" ? (
                                    <Input
                                      value={editingNumberScale}
                                      onChange={(event) =>
                                        setEditingNumberScale(
                                          event.currentTarget.value
                                            .replace(/[^\d]/g, "")
                                            .slice(0, 1),
                                        )
                                      }
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                      className="h-8 rounded-lg border-border/70 bg-background shadow-none"
                                    />
                                  ) : null}
                                </div>
                              ) : null}
                              {editingType === "relation" ? (
                                <RelationConfigFields
                                  allowCrossOntology={
                                    editingRelationAllowCrossOntology
                                  }
                                  singleSelect={editingRelationSingleSelect}
                                  allowedOntologyIds={
                                    editingRelationAllowedOntologyIds
                                  }
                                  ontologyOptions={ontologyOptions}
                                  translate={translate}
                                  onAllowCrossOntologyChange={
                                    setEditingRelationAllowCrossOntology
                                  }
                                  onSingleSelectChange={
                                    setEditingRelationSingleSelect
                                  }
                                  onAllowedOntologyIdsChange={
                                    setEditingRelationAllowedOntologyIds
                                  }
                                />
                              ) : null}
                            </div>
                          ) : (
                            <>
                              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                <span className="truncate text-[13px] font-medium text-foreground">
                                  {column.name}
                                </span>
                                {column.readOnly ? (
                                  <Badge
                                    variant="outline"
                                    className="rounded-full px-2 py-0.5 text-[10px] font-medium"
                                  >
                                    {translate(
                                      "components.data-v2.feature-column.incoming-relation-editable",
                                    )}
                                  </Badge>
                                ) : null}
                                <Badge
                                  variant="secondary"
                                  className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
                                >
                                  {typeOptions.find(
                                    (option) => option.id === column.type,
                                  )?.label || column.type}
                                </Badge>
                              </div>
                              <div className="mt-0.5 truncate text-[11px] leading-4 text-muted-foreground">
                                {column.description || column.type}
                              </div>
                            </>
                          )}
                        </div>
                        {!editing ? (
                          <div className="flex shrink-0 items-center gap-0.5">
                            <div
                              className={
                                "mr-1 flex h-4 w-4 items-center justify-center rounded-full border transition-colors " +
                                (selected
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-border/70 bg-background text-transparent")
                              }
                            >
                              <Check className="h-2.5 w-2.5" />
                            </div>
                            {!column.readOnly ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-md"
                                disabled={!canUpdate}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  startEdit(column);
                                }}
                                aria-label={copy(
                                  model,
                                  "editLabelKey",
                                  "Edit",
                                  translate,
                                )}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            ) : null}
                            {!column.readOnly ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-md"
                                disabled={!canCreate}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  createColumn(column);
                                }}
                                aria-label={copy(
                                  model,
                                  "duplicateLabelKey",
                                  "Duplicate",
                                  translate,
                                )}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            ) : null}
                            {!column.readOnly ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-md text-destructive-text hover:text-destructive-text"
                                disabled={!canDelete}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDeleteConfirm([column]);
                                }}
                                aria-label={copy(
                                  model,
                                  "deleteLabelKey",
                                  "Delete",
                                  translate,
                                )}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {pagination.hasMore === true &&
                  readText(pagination.nextCursor) ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={pagination.loadingMore === true}
                      onClick={() =>
                        emitIntent(
                          "capability.feature-column-manager.load-more",
                          { cursor: readText(pagination.nextCursor) },
                        )
                      }
                    >
                      {pagination.loadingMore === true
                        ? "…"
                        : translate("common.actions.load-more")}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border/70 bg-background px-4 py-7 text-center text-sm text-muted-foreground">
                  {copy(
                    model,
                    "emptyLabelKey",
                    "No feature columns",
                    translate,
                  )}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <AlertDialog
          open={confirmDeleteOpen}
          onOpenChange={setConfirmDeleteOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {translate("components.data-v2.feature-column.delete-title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTargets.length === 1
                  ? translate(
                      "components.data-v2.feature-column.delete-single-desc",
                      { name: deleteTargets[0]?.name || "" },
                    )
                  : translate(
                      "components.data-v2.feature-column.delete-multi-desc",
                      { count: deleteTargets.length },
                    )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {copy(model, "cancelLabelKey", "Cancel", translate)}
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={!deleteTargets.length}
                  onClick={() => deleteColumns(deleteTargets)}
                >
                  {copy(model, "deleteLabelKey", "Delete", translate)}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  };
